import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, serverTimestamp, addDoc, collection, deleteDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  X,
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Globe,
  Briefcase,
  Star,
  Link as LinkIcon,
  Users,
  UserCheck,
  Loader2,
  Trash2,
  Download,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import StageSelector from '../ui/StageSelector';
import { normalizeBusinessStage } from '../../constants/businessStages';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const ClientEditForm = ({ client, onClose, onSaved }) => {
  const isEditMode = Boolean(client?.id);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [initialForm, setInitialForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [closeConfirm, setCloseConfirm] = useState({ isOpen: false });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [touched, setTouched] = useState({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  useEffect(() => {
    const nextInitial = {
      firstname: client?.firstname || '',
      lastname: client?.lastname || '',
      surname: client?.surname || '',
      name: client?.name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      organization: client?.organization || '',
      location: client?.location || '',
      country: client?.country || '',
      program: client?.program || '',
      businessStage: normalizeBusinessStage(client?.businessStage || ''),
      role: client?.role || '',
      status: client?.status || 'active',
      bio: client?.bio || '',
      profileLink: client?.profileLink || '',
      cohortGroup: client?.cohortGroup || client?.corhotGroup || '', // normalize
      assigned: client?.assigned || '',
      assignedEmail: client?.assignedEmail || '',
      assignedCoachName: client?.assignedCoachName || '',
      assignedCoachEmail: client?.assignedCoachEmail || '',
    };
    setForm(nextInitial);
    setInitialForm(nextInitial);
    setErrors({});
    setTouched({});
    setIsDirty(false);
    setShowErrorSummary(false);
  }, [client]);

  const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
  const validateURL = url => {
    if (!url) return true;
    try {
      const candidate = url.match(/^https?:\/\//i) ? url : `https://${url}`;
      new URL(candidate);
      return true;
    } catch {
      return false;
    }
  };

  const requiredFields = useMemo(() => ['firstname', 'lastname', 'name', 'email'], []);

  const validators = useMemo(
    () => ({
      firstname: v => (!!String(v).trim() ? '' : 'First name is required'),
      lastname: v => (!!String(v).trim() ? '' : 'Last name is required'),
      name: v => (!!String(v).trim() ? '' : 'Display name is required'),
      email: v =>
        !String(v).trim()
          ? 'Email is required'
          : validateEmail(v)
            ? ''
            : 'Enter a valid email address',
      assignedEmail: v => (v && !validateEmail(v) ? 'Enter a valid email address' : ''),
      assignedCoachEmail: v => (v && !validateEmail(v) ? 'Enter a valid email address' : ''),
      profileLink: v =>
        v && !validateURL(v) ? 'Enter a valid URL (e.g., linkedin.com/in/...)' : '',
    }),
    []
  );

  const runFieldValidation = useCallback(
    (key, value) => {
      if (!validators[key]) return;
      const message = validators[key](value);
      setErrors(prev => {
        const next = { ...prev };
        if (message) next[key] = message;
        else delete next[key];
        return next;
      });
    },
    [validators]
  );

  const validateAll = useCallback(
    f => {
      const nextErrors = {};
      Object.keys(validators).forEach(k => {
        const msg = validators[k](f[k]);
        if (msg) nextErrors[k] = msg;
      });
      setErrors(nextErrors);
      return nextErrors;
    },
    [validators]
  );

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
    if (touched[key] || requiredFields.includes(key) || errors[key]) {
      runFieldValidation(key, value);
    }
  };

  const handleBlur = key => {
    setTouched(t => ({ ...t, [key]: true }));
    runFieldValidation(key, form[key]);
  };

  const trimmedPayload = src => {
    const out = {};
    Object.entries(src || {}).forEach(([k, v]) => {
      out[k] = typeof v === 'string' ? v.trim() : v;
    });
    return out;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const found = validateAll(form);
    if (Object.keys(found).length > 0) {
      setShowErrorSummary(true);
      toast.error('Please resolve the highlighted errors.');
      return;
    }

    setSaving(true);
    const toastId = toast.loading(isEditMode ? 'Saving client...' : 'Creating client...');

    try {
      const payload = {
        ...trimmedPayload({
          ...form,
          corhotGroup: form.cohortGroup ?? form.corhotGroup ?? '',
          businessStage: normalizeBusinessStage(form.businessStage),
        }),
        updatedAt: serverTimestamp(),
      };

      if (isEditMode) {
        const clientRef = doc(firestore, 'clients', client.id);
        await updateDoc(clientRef, payload);
        toast.update(toastId, {
          render: 'Client information saved successfully.',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
        onSaved?.({ ...payload, id: client.id });
      } else {
        const createPayload = {
          ...payload,
          createdAt: serverTimestamp(),
        };
        const created = await addDoc(collection(firestore, 'clients'), createPayload);
        toast.update(toastId, {
          render: 'Client created successfully.',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
        onSaved?.({ ...createPayload, id: created.id });
      }

      setIsDirty(false);
      setTimeout(() => onClose?.(), 900);
    } catch (err) {
      toast.update(toastId, {
        render: 'Failed to save client. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      setCloseConfirm({ isOpen: true });
      return;
    }
    onClose?.();
  };

  const handleCloseConfirm = () => {
    setCloseConfirm({ isOpen: false });
    onClose?.();
  };

  const handleCloseCancel = () => {
    setCloseConfirm({ isOpen: false });
  };

  const handleDeleteClick = () => {
    if (!isEditMode || !client?.id) return;
    setDeleteConfirm({ isOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (!isEditMode || !client?.id) return;

    setIsDeleting(true);
    const toastId = toast.loading('Deleting client...');
    try {
      const clientRef = doc(firestore, 'clients', client.id);
      await deleteDoc(clientRef);
      setDeleteConfirm({ isOpen: false });
      toast.update(toastId, {
        render: 'Client deleted.',
        type: 'success',
        isLoading: false,
        autoClose: 2000,
      });
      onSaved?.(null);
      setTimeout(() => onClose?.(), 600);
    } catch (err) {
      toast.update(toastId, {
        render: 'Failed to delete client. Please try again.',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false });
  };

  const handleExport = () => {
    const data = { ...(isEditMode ? { id: client.id } : {}), ...trimmedPayload(form) };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${form.lastname || form.firstname || 'client'}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    if (!initialForm) return;
    setForm(initialForm);
    setErrors({});
    setTouched({});
    setIsDirty(false);
    setShowErrorSummary(false);
  };

  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (!saving) {
          const fakeEvent = { preventDefault: () => {} };
          handleSubmit(fakeEvent);
        }
      }
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, form, isDirty]);

  const inputClass = fieldName =>
    [
      'w-full rounded-lg border px-3 py-2.5 text-sm shadow-sm transition',
      errors[fieldName]
        ? 'border-red-500 bg-red-50 focus:border-red-600 focus:ring-2 focus:ring-red-600/20'
        : 'border-gray-300 bg-white hover:border-gray-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
      'placeholder:text-gray-400 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed',
    ].join(' ');

  const FormSection = ({ title, icon: Icon, children }) => (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-blue-600" />
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="space-y-4">{children}</div>
    </motion.section>
  );

  const InputField = ({
    label,
    fieldName,
    type = 'text',
    icon: Icon,
    placeholder,
    rows,
    autoComplete,
  }) => {
    const id = `field-${fieldName}`;
    const describedBy = errors[fieldName] ? `${id}-error` : undefined;
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700" htmlFor={id}>
          {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
          <span>{label}</span>
          {['firstname', 'lastname', 'name', 'email'].includes(fieldName) && (
            <span className="text-red-500" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {rows ? (
          <textarea
            id={id}
            rows={rows}
            className={inputClass(fieldName)}
            value={form[fieldName] || ''}
            onChange={e => updateField(fieldName, e.target.value)}
            onBlur={() => handleBlur(fieldName)}
            placeholder={placeholder}
            disabled={saving}
            aria-invalid={Boolean(errors[fieldName])}
            aria-describedby={describedBy}
          />
        ) : (
          <input
            id={id}
            type={type}
            className={inputClass(fieldName)}
            value={form[fieldName] || ''}
            onChange={e => updateField(fieldName, e.target.value)}
            onBlur={() => handleBlur(fieldName)}
            placeholder={placeholder}
            disabled={saving}
            autoComplete={autoComplete}
            aria-invalid={Boolean(errors[fieldName])}
            aria-describedby={describedBy}
            inputMode={type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined}
          />
        )}
        {errors[fieldName] && (
          <motion.p
            id={`${id}-error`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="text-sm text-red-600"
          >
            {errors[fieldName]}
          </motion.p>
        )}
      </div>
    );
  };

  // ---------- RENDER ----------
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-gradient-to-b from-white to-gray-50 shadow-2xl ring-1 ring-black/5 sm:max-w-xl md:max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-form-title"
      >
        {/* Header */}
        <div className="border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 id="client-form-title" className="text-lg font-bold text-gray-900 sm:text-xl">
                {isEditMode ? 'Edit Client Profile' : 'Create Client Profile'}
              </h3>
              <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
                {isEditMode
                  ? 'Update client information and assignments.'
                  : 'Enter client information and assignments.'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Optional Error Summary */}
          {showErrorSummary && Object.keys(errors).length > 0 && (
            <div className="mx-4 mt-3 sm:mx-6">
              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">Please fix the following:</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {Object.entries(errors).map(([k, v]) => (
                      <li key={k}>
                        <span className="font-medium">{k}</span>: {v}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6 p-4 sm:p-6">
            {/* Personal Information */}
            <FormSection title="Personal Information" icon={User}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <InputField
                  label="First Name"
                  fieldName="firstname"
                  icon={User}
                  placeholder="Enter first name"
                  autoComplete="given-name"
                />
                <InputField
                  label="Last Name"
                  fieldName="lastname"
                  icon={User}
                  placeholder="Enter last name"
                  autoComplete="family-name"
                />
                <InputField
                  label="Surname"
                  fieldName="surname"
                  icon={User}
                  placeholder="Enter surname (optional)"
                  autoComplete="off"
                />
                <div className="sm:col-span-2 xl:col-span-3">
                  <InputField
                    label="Display Name"
                    fieldName="name"
                    icon={User}
                    placeholder="Enter display name"
                    autoComplete="nickname"
                  />
                </div>
              </div>
            </FormSection>

            {/* Contact Information */}
            <FormSection title="Contact Information" icon={Mail}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Email Address"
                  fieldName="email"
                  type="email"
                  icon={Mail}
                  placeholder="name@company.com"
                  autoComplete="email"
                />
                <InputField
                  label="Phone Number"
                  fieldName="phone"
                  type="tel"
                  icon={Phone}
                  placeholder="+267 7X XXX XXX"
                  autoComplete="tel"
                />
              </div>
            </FormSection>

            {/* Organization & Location */}
            <FormSection title="Organization & Location" icon={Building}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Organization"
                  fieldName="organization"
                  icon={Building}
                  placeholder="Company or institution"
                  autoComplete="organization"
                />
                <InputField
                  label="Role"
                  fieldName="role"
                  icon={Briefcase}
                  placeholder="Role/position"
                  autoComplete="organization-title"
                />
                <InputField
                  label="Location"
                  fieldName="location"
                  icon={MapPin}
                  placeholder="City, State/Region"
                  autoComplete="address-level2"
                />
                <InputField
                  label="Country"
                  fieldName="country"
                  icon={Globe}
                  placeholder="Country"
                  autoComplete="country-name"
                />
              </div>
            </FormSection>

            {/* Program Information */}
            <FormSection title="Program Information" icon={Star}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <InputField
                  label="Program"
                  fieldName="program"
                  icon={Star}
                  placeholder="Program name"
                />
                <div className="xl:col-span-2">
                  <StageSelector
                    label="Business Stage"
                    value={form.businessStage || ''}
                    onChange={v => updateField('businessStage', v)}
                  />
                </div>
                <InputField
                  label="Cohort Group"
                  fieldName="cohortGroup"
                  icon={Users}
                  placeholder="Cohort/Group name"
                />
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Star className="h-4 w-4" aria-hidden="true" />
                    Status
                  </label>
                  <select
                    className={inputClass('status')}
                    value={form.status || 'active'}
                    onChange={e => updateField('status', e.target.value)}
                    onBlur={() => handleBlur('status')}
                    disabled={saving}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
            </FormSection>

            {/* Additional Information */}
            <FormSection title="Additional Information" icon={LinkIcon}>
              <div className="space-y-4">
                <InputField
                  label="Profile Link"
                  fieldName="profileLink"
                  icon={LinkIcon}
                  placeholder="linkedin.com/in/username"
                  autoComplete="url"
                />
                <InputField
                  label="Bio"
                  fieldName="bio"
                  placeholder="Short bio (optional)"
                  rows={4}
                />
              </div>
            </FormSection>

            {/* Assignments */}
            <FormSection title="Staff Assignments" icon={UserCheck}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-800">Moderator Assignment</h5>
                  <InputField
                    label="Moderator Name"
                    fieldName="assigned"
                    icon={User}
                    placeholder="Moderator name"
                  />
                  <InputField
                    label="Moderator Email"
                    fieldName="assignedEmail"
                    type="email"
                    icon={Mail}
                    placeholder="moderator@company.com"
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-800">Coach Assignment</h5>
                  <InputField
                    label="Coach Name"
                    fieldName="assignedCoachName"
                    icon={User}
                    placeholder="Coach name"
                  />
                  <InputField
                    label="Coach Email"
                    fieldName="assignedCoachEmail"
                    type="email"
                    icon={Mail}
                    placeholder="coach@company.com"
                    autoComplete="email"
                  />
                </div>
              </div>
            </FormSection>

            <div className="border-t border-gray-200 pt-4 sm:pt-6">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="submit"
                    disabled={saving || Object.keys(errors).length > 0}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {isEditMode ? 'Saving...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4" />
                        {isEditMode ? 'Save Changes' : 'Create Client'}
                      </>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="button"
                    onClick={handleClose}
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </motion.button>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="button"
                    onClick={handleReset}
                    disabled={saving || !isDirty}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset Changes
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="button"
                    onClick={handleExport}
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Download className="h-4 w-4" />
                    Export JSON
                  </motion.button>
                  {isEditMode && (
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      type="button"
                      onClick={handleDeleteClick}
                      disabled={saving}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 transition-all duration-200 hover:border-red-400 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Client
                    </motion.button>
                  )}
                </div>
                {isDirty && (
                  <p className="mt-1 text-center text-xs text-amber-700 sm:text-sm">
                    You have unsaved changes.
                  </p>
                )}
              </div>
            </div>
          </form>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastClassName="font-medium"
        />

        <ConfirmationModal
          isOpen={closeConfirm.isOpen}
          onClose={handleCloseCancel}
          onConfirm={handleCloseConfirm}
          title="Unsaved Changes"
          message="You have unsaved changes. Close without saving?"
          confirmText="Close"
          cancelText="Cancel"
          confirmButtonColor="bg-gray-600 hover:bg-gray-700"
          isLoading={false}
        />

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Client"
          message="This action will permanently delete this client. Continue?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </motion.div>
    </div>
  );
};

export default ClientEditForm;
